const chapter = {
  id: 'p-block-17',
  slug: 'p-block-17',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'p-Block — Group 17 (Halogens) & Group 18 (Noble Gases)',
        hindi: 'p-खण्ड — वर्ग 17 (हैलोजन) और वर्ग 18 (अक्रिय गैसें)',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2–3 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/एनईईटी में 2-3 प्रश्न',
  },
  syllabusTags: ['Inorganic Chemistry', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'general-halogens',
      title: {
        english: 'General — F, Cl, Br, I, At',
        hindi: 'सामान्य - एफ, सीएल, ब्र, आई, एट',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Configuration $ns^2 np^5$ — one electron short of noble-gas config; most reactive non-metals.',
        hindi: 'कॉन्फ़िगरेशन $ns^2 np^5$ - नोबल-गैस कॉन्फ़िगरेशन से एक इलेक्ट्रॉन कम; सर्वाधिक प्रतिक्रियाशील अधातुएँ।',
            },
            {
              english: 'Common O.S.: F only $-1$ (no d-orbitals, highest EN); others $-1, +1, +3, +5, +7$.',
        hindi: 'सामान्य O.S.: F केवल $-1$ (कोई d-ऑर्बिटल्स नहीं, उच्चतम EN); अन्य $-1, +1, +3, +5, +7$.',
            },
            {
              english: 'Diatomic molecules $X_2$. Physical state at RT: F₂ gas (pale yellow), Cl₂ gas (greenish yellow), Br₂ liquid (red-brown, only liquid non-metal), I₂ solid (purple-black, sublimes).',
        hindi: 'द्विपरमाणुक अणु $X_2$। आरटी पर भौतिक अवस्था: एफ₂ गैस (हल्का पीला), सीएल₂ गैस (हरा पीला), बीआर₂ तरल (लाल-भूरा, केवल तरल गैर-धातु), आई₂ ठोस (बैंगनी-काला, उदात्त)।',
            },
            {
              english: 'EN order: $F > O > Cl > N > Br > I$. F is most electronegative element.',
        hindi: 'EN आदेश: $F > O > Cl > N > Br > I$. F सर्वाधिक विद्युत ऋणात्मक तत्व है।',
            },
            {
              english: 'Bond dissociation energy: $Cl_2 > Br_2 > F_2 > I_2$. F₂ anomalously **low** (small size → lone-pair repulsion in F–F).',
        hindi: 'बॉन्ड पृथक्करण ऊर्जा: $Cl_2 > Br_2 > F_2 > I_2$। F₂ असामान्य रूप से **कम** (छोटा आकार → F–F में अकेला-जोड़ी प्रतिकर्षण)।',
            },
            {
              english: 'Hydration & lattice energies make F₂ overall the strongest oxidising agent: $F_2 > Cl_2 > Br_2 > I_2$.',
        hindi: 'हाइड्रेशन और Common Trapी ऊर्जा F₂ को समग्र रूप से सबसे मजबूत ऑक्सीकरण एजेंट बनाती है: $F_2 > Cl_2 > Br_2 > I_2$।',
            },
          ],
        },
        {
          type: 'table',
          headers: ['Property', 'F', 'Cl', 'Br', 'I'],
          rows: [
            ['Atomic radius (pm)', '64', '99', '114', '133'],
            ['EN', '4.0', '3.0', '2.8', '2.5'],
            ['$E^\\circ$ (V)', '+2.87', '+1.36', '+1.08', '+0.54'],
            ['Bond E (kJ/mol)', '158', '243', '192', '151'],
          ],
        },
      ],
    },
    {
      id: 'chlorine',
      title: {
        english: 'Chlorine — Preparation & Properties',
        hindi: 'क्लोरीन — तैयारी और गुणधर्म',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Lab prep: $MnO_2 + 4HCl \\to MnCl_2 + Cl_2 + 2H_2O$ (or with KMnO₄).',
        hindi: 'लैब तैयारी: $MnO_2 + 4HCl \\to MnCl_2 + Cl_2 + 2H_2O$ (या KMnO₄ के साथ)।',
            },
            {
              english: 'Industrial: **electrolysis** of brine (Castner–Kellner / membrane cell): cathode H₂, anode Cl₂.',
        hindi: 'औद्योगिक: नमकीन पानी का **इलेक्ट्रोलिसिस** (कास्टनर-केल्नर / झिल्ली कोशिका): कैथोड H₂, एनोड Cl₂।',
            },
            {
              english: 'Deacon process: $4HCl + O_2 \\xrightarrow{CuCl_2, 723\\,K} 2Cl_2 + 2H_2O$.',
        hindi: 'डीकन प्रक्रिया: $4HCl + O_2 \\xrightarrow{CuCl_2, 723\\,K} 2Cl_2 + 2H_2O$.',
            },
            {
              english: 'Reactions: with H₂O — $Cl_2 + H_2O \\to HCl + HOCl$ (HOCl bleaches by oxidation, **permanent bleaching**).',
        hindi: 'प्रतिक्रियाएं: H₂O के साथ - $Cl_2 + H_2O \\to HCl + HOCl$ (HOCl ऑक्सीकरण द्वारा ब्लीच करता है, **स्थायी ब्लीचिंग**)।',
            },
            {
              english: 'With cold dilute NaOH: $Cl_2 + 2NaOH \\to NaCl + NaOCl + H_2O$ (bleach).',
        hindi: 'ठंडे तनु NaOH के साथ: $Cl_2 + 2NaOH \\to NaCl + NaOCl + H_2O$ (ब्लीच)।',
            },
            {
              english: 'With hot conc NaOH: $3Cl_2 + 6NaOH \\to 5NaCl + NaClO_3 + 3H_2O$.',
        hindi: 'गर्म सान्द्र NaOH के साथ: $3Cl_2 + 6NaOH \\to 5NaCl + NaClO_3 + 3H_2O$।',
            },
            {
              english: 'With dry slaked lime → **bleaching powder** $Ca(OCl)Cl$.',
        hindi: 'सूखे बुझे हुए चूने के साथ → **ब्लीचिंग पाउडर** $Ca(OCl)Cl$।',
            },
          ],
        },
      ],
    },
    {
      id: 'hydrogen-halides',
      title: {
        english: 'Hydrogen Halides — HX',
        hindi: 'हाइड्रोजन हैलाइड — HX',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Property', 'HF', 'HCl', 'HBr', 'HI'],
          rows: [
            ['B.P. (K)', '293', '189', '206', '238'],
            ['Bond energy (kJ/mol)', '566', '431', '366', '299'],
            ['Acidity in water', 'Weak', 'Strong', 'Stronger', 'Strongest'],
            ['Thermal stability', 'Highest', '↓', '↓', 'Lowest'],
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: 'HF anomalously high B.P. due to **H-bonding** (zig-zag chains).',
        hindi: 'एचएफ असामान्य रूप से उच्च बी.पी. **H-बॉन्डिंग** (ज़िग-ज़ैग चेन) के कारण।',
            },
            {
              english: 'Acidic strength in water: $HF \\ll HCl < HBr < HI$ (bond energy decreases ⇒ easier H⁺ release).',
        hindi: 'पानी में अम्लीय शक्ति: $HF \\ll HCl < HBr < HI$ (बंध ऊर्जा कम हो जाती है ⇒ आसान H⁺ रिलीज)।',
            },
            {
              english: 'HF etches glass: $SiO_2 + 4HF \\to SiF_4 + 2H_2O$ — stored in wax/PTFE bottles.',
        hindi: 'एचएफ एचेज़ ग्लास: $SiO_2 + 4HF \\to SiF_4 + 2H_2O$ - मोम/पीटीएफई बोतलों में संग्रहित।',
            },
            {
              english: 'HBr, HI cannot be made by conc H₂SO₄ + NaBr/NaI (oxidised to Br₂/I₂); use $H_3PO_4$ instead.',
        hindi: 'HBr, HI सांद्र H₂SO₄ + NaBr/NaI (Br₂/I₂ में ऑक्सीकृत) द्वारा नहीं बनाया जा सकता; इसके बजाय $H_3PO_4$ का उपयोग करें।',
            },
          ],
        },
      ],
    },
    {
      id: 'oxoacids-halogens',
      title: {
        english: 'Interhalogens & Oxoacids',
        hindi: 'अन्तरहैलोजन और ऑक्सोअम्ल',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Type', 'Examples', 'Shape'],
          rows: [
            ['$XX\'$', 'ClF, BrCl, ICl, BrF', 'Linear'],
            ['$XX\'_3$', 'ClF₃, BrF₃, IF₃', 'Bent T (sp³d)'],
            ['$XX\'_5$', 'IF₅, BrF₅', 'Square pyramidal (sp³d²)'],
            ['$XX\'_7$', 'IF₇', 'Pentagonal bipyramidal (sp³d³)'],
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: 'Larger atom is central; reactivity > the parent halogens (weaker bond).',
        hindi: 'बड़ा परमाणु केंद्रीय है; प्रतिक्रियाशीलता > मूल हैलोजन (कमजोर बंधन)। **पॉलीहैलाइड्स**: (रैखिक) - द्वारा निर्मित।',
            },
            {
              english: '**Polyhalides**: $I_3^-$ (linear) — formed by $KI + I_2 \\to KI_3$.',
        hindi: '**पॉलीहैलाइड्स**: $I_3^-$ (रैखिक) - $KI + I_2 \\to KI_3$ द्वारा निर्मित।',
            },
            {
              english: 'Oxoacids of Cl: HOCl (+1), HOClO / HClO₂ (+3), HClO₃ (+5), HClO₄ (+7).',
        hindi: 'सीएल के ऑक्सोएसिड: HOCl (+1), HOClO / HClO₂ (+3), HClO₃ (+5), HClO₄ (+7)।',
            },
            {
              english: 'Acidic strength: $HClO < HClO_2 < HClO_3 < HClO_4$ (more O ⇒ better stabilisation of conjugate base).',
        hindi: 'अम्लीय ताकत: $HClO < HClO_2 < HClO_3 < HClO_4$ (अधिक O ⇒ संयुग्म आधार का बेहतर स्थिरीकरण)।',
            },
            {
              english: 'Oxidising power: $HClO > HClO_2 > HClO_3 > HClO_4$ (opposite).',
        hindi: 'ऑक्सीकरण शक्ति: $HClO > HClO_2 > HClO_3 > HClO_4$ (विपरीत)।',
            },
            {
              english: 'F has only one oxoacid: HOF.',
        hindi: 'F में केवल एक ऑक्सोएसिड है: HOF।',
            },
          ],
        },
      ],
    },
    {
      id: 'noble-gases',
      title: {
        english: 'Group 18 — Noble Gases (Brief)',
        hindi: 'वर्ग 18 — अक्रिय गैसें (संक्षिप्त)',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'He, Ne, Ar, Kr, Xe, Rn — closed shell $ns^2 np^6$ (He: $1s^2$).',
        hindi: 'He, Ne, Ar, Kr, Xe, Rn - बंद कोश $ns^2 np^6$ (He: $1s^2$)।',
            },
            {
              english: 'Monoatomic, colourless, very low BP & MP, very high IE (He highest).',
        hindi: 'मोनोआटोमिक, रंगहीन, बहुत कम बीपी और एमपी, बहुत उच्च IE (वह उच्चतम)।',
            },
            {
              english: 'Atmospheric Ar by fractional distillation of liquid air; He from natural gas.',
        hindi: 'तरल हवा के आंशिक आसवन द्वारा वायुमंडलीय Ar; वह प्राकृतिक गैस से.',
            },
            {
              english: 'Compounds: only Xe (and a few Kr) form true compounds with F & O (high-EN, small).',
        hindi: 'यौगिक: केवल Xe (और कुछ Kr) F और O (उच्च-EN, छोटे) के साथ वास्तविक यौगिक बनाते हैं।',
            },
            {
              english: '$XeF_2$ — linear ($sp^3d$, 3 lp).',
        hindi: '$XeF_2$ — रैखिक ($sp^3d$, 3 एलपी)।',
            },
            {
              english: '$XeF_4$ — square planar ($sp^3d^2$, 2 lp).',
        hindi: '$XeF_4$ — वर्ग तलीय ($sp^3d^2$, 2 एलपी)।',
            },
            {
              english: '$XeF_6$ — distorted octahedral ($sp^3d^3$, 1 lp).',
        hindi: '$XeF_6$ — विकृत अष्टफलकीय ($sp^3d^3$, 1 एलपी)।',
            },
            {
              english: '$XeO_3$ — pyramidal ($sp^3$, 1 lp); $XeO_4$ — tetrahedral.',
        hindi: '$XeO_3$ — पिरामिडनुमा ($sp^3$, 1 एलपी); $XeO_4$ - चतुष्फलकीय।',
            },
            {
              english: 'Hydrolysis: $XeF_6 + 3H_2O \\to XeO_3 + 6HF$; partial: $XeF_6 + H_2O \\to XeOF_4 + 2HF$.',
        hindi: 'हाइड्रोलिसिस: $XeF_6 + 3H_2O \\to XeO_3 + 6HF$; आंशिक: $XeF_6 + H_2O \\to XeOF_4 + 2HF$.',
            },
            {
              english: 'Uses: He (balloons, low-T cryogen for NMR/MRI), Ne (signs), Ar (inert atmosphere in welding & bulbs), Kr & Xe (lights), Rn (radiotherapy).',
        hindi: 'उपयोग: वह (गुब्बारे, NMR/MRI के लिए निम्न-टी क्रायोजेन), Ne (संकेत), Ar (वेल्डिंग और बल्बों में निष्क्रिय वातावरण), Kr और Xe (रोशनी), Rn (रेडियोथेरेपी)।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'F_2 > Cl_2 > Br_2 > I_2',
      desc: {
        english: 'Oxidising power',
        hindi: 'ऑक्सीकरण शक्ति',
      },
    },
    {
      latex: 'HF \\ll HCl < HBr < HI',
      desc: {
        english: 'Acidic strength of HX',
        hindi: 'HX की अम्लीय शक्ति',
      },
    },
    {
      latex: 'HClO < HClO_2 < HClO_3 < HClO_4',
      desc: {
        english: 'Acidity of Cl-oxoacids',
        hindi: 'सीएल-ऑक्सोएसिड की अम्लता',
      },
    },
    {
      latex: '2NaCl + 2H_2O \\xrightarrow{\\text{electrol.}} 2NaOH + Cl_2 + H_2',
      desc: {
        english: 'Brine electrolysis',
        hindi: 'ब्राइन इलेक्ट्रोलिसिस',
      },
    },
    {
      latex: '3Cl_2 + 6NaOH(\\text{hot, conc}) \\to 5NaCl + NaClO_3 + 3H_2O',
      desc: {
        english: 'Disproportionation',
        hindi: 'अनुपातहीनता',
      },
    },
    {
      latex: 'XeF_6 + 3H_2O \\to XeO_3 + 6HF',
      desc: {
        english: 'Complete hydrolysis',
        hindi: 'पूर्ण हाइड्रोलिसिस',
      },
    },
    {
      latex: 'SiO_2 + 4HF \\to SiF_4 + 2H_2O',
      desc: {
        english: 'HF etches glass',
        hindi: 'एचएफ ग्लास को खोदता है',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Structure',
      problem: {
        english: 'Shape of XeF₄ is:',
        hindi: 'XeF₄ का आकार है:',
      },
      answer: {
        english: 'Square planar',
        hindi: 'वर्गाकार तलीय',
      },
      solution: {
        english: '$sp^3d^2$ with two lone pairs.',
        hindi: '$sp^3d^2$ दो अकेले जोड़े के साथ।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Acidity',
      problem: {
        english: 'Strongest acid in water:',
        hindi: 'पानी में सबसे मजबूत एसिड:',
      },
      answer: {
        english: 'HI',
        hindi: 'नमस्ते',
      },
      solution: {
        english: 'Lowest H–X bond energy.',
        hindi: 'सबसे कम H-X बांड ऊर्जा।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Bleaching',
      problem: {
        english: 'Permanent bleaching action of chlorine is due to:',
        hindi: 'क्लोरीन की स्थायी ब्लीचिंग क्रिया निम्न के कारण होती है:',
      },
      answer: {
        english: 'Oxidation by nascent oxygen from HOCl',
        hindi: 'HOCl से नवजात ऑक्सीजन द्वारा ऑक्सीकरण',
      },
      solution: {
        english: '$HOCl \\to HCl + [O]$.',
        hindi: '$HOCl \\to HCl + [O]$।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Halogen',
      problem: {
        english: 'Only liquid non-metal at RT:',
        hindi: 'आरटी पर केवल तरल अधातु:',
      },
      answer: {
        english: 'Bromine',
        hindi: 'ब्रोमीन',
      },
      solution: {
        english: 'Br₂ red-brown liquid.',
        hindi: 'Br₂ लाल-भूरा तरल।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Oxoacid',
      problem: {
        english: 'Acid not formed by fluorine:',
        hindi: 'फ्लोरीन से एसिड नहीं बनता:',
      },
      answer: {
        english: '$HFO_3$',
        hindi: '$HFO_3$',
      },
      solution: {
        english: 'F shows only $-1$ and $0$; only HOF exists.',
        hindi: 'F केवल $-1$ और $0$ दिखाता है; केवल HOF मौजूद है। **डोबेराइनर के त्रिक** (1817): समान गुणों वाले 3 तत्वों के समूह; मध्य तत्व का परमाणु द्रव्यमान ≈ अन्य दो (Li, Na, K) का माध्य। **न्यूलैंड्स का अष्टक का नियम** (1865): प्रत्येक 8वाँ तत्व समान गुण दिखाता है - Ca के बाद विफल हो जाता है।',
      },
    },
  ],
  next: {
    slug: 'd-and-f-block',
    title: 'd- and f-Block Elements',
  },
};

export default chapter;
